/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { Avatar } from "../../shared/Avatar";
import { compareDay, wallTime, type DayRow, type Flag } from "./flags";
import type { HoursEntry, HoursPerson } from "./types";

type Props = {
  person: HoursPerson;
  timezone: string;
  canSeeWages: boolean;
  onEdit: (entry: HoursEntry) => void;
  onAdd: (defaults: { date: string; start: string; end: string; shift_id: number | null } | null) => void;
};

const FLAG_STYLE: Record<Flag, string> = {
  late: "badge-warning",
  left_early: "badge-warning",
  no_show: "badge-error",
  still_in: "badge-error",
  unplanned: "badge-info",
};

const dayLabel = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

const fmt = (n: number) => n.toFixed(1);

// One person's week: planned vs worked, and each shift next to its clock entry.
export const PersonHours = defineComponent<Props>({
  name: "PersonHours",
  props: ["person", "timezone", "canSeeWages", "onEdit", "onAdd"],
  setup(props) {
    const { t } = useLocalization();

    const rows = computed(() => {
      const p = props.person;
      const now = new Date().toISOString();
      const dates = [...new Set([...p.planned.map((s) => s.date), ...p.entries.map((e) => e.date)])].sort();
      return dates.flatMap((date) =>
        compareDay(
          p.planned.filter((s) => s.date === date),
          p.entries.filter((e) => e.date === date),
          props.timezone,
          now,
        ).map((r) => ({ date, ...r })),
      );
    });
    const flagged = computed(() => rows.value.filter((r) => r.flags.length).length);
    const open = ref(false);

    const plannedOf = (r: DayRow) => props.person.planned.find((s) => s.shift_id === r.planned?.shift_id);
    const entryOf = (r: DayRow) => props.person.entries.find((e) => e.id === r.entry?.id);

    const flagText = (f: Flag, r: DayRow) =>
      f === "late" ? t("workday.hours.flag_late").replace("{minutes}", String(r.lateBy ?? 0)) : t(`workday.hours.flag_${f}`);

    return () => {
      const p = props.person;
      const diff = p.worked_hours - p.planned_hours;
      return (
        <div class="rounded-2xl border border-base-300 bg-base-100">
          <button class="flex w-full items-center gap-3 p-4 text-left" onClick={() => (open.value = !open.value)}>
            <Avatar name={p.name} id={p.staff_id} color={p.color} size="md" />
            <div class="min-w-0 flex-1">
              <div class="truncate font-semibold">{p.name}</div>
              {flagged.value > 0 ? (
                <div class="flex items-center gap-1 text-xs text-warning">
                  <span class="material-symbols-outlined text-sm">flag</span>
                  {t("workday.hours.to_check").replace("{count}", String(flagged.value))}
                </div>
              ) : (
                <div class="text-xs text-base-content/50">{t("workday.hours.all_good")}</div>
              )}
            </div>
            <div class="grid grid-cols-3 gap-4 text-right sm:gap-8">
              <div>
                <div class="text-xs text-base-content/50">{t("workday.hours.planned")}</div>
                <div class="font-medium tabular-nums">{fmt(p.planned_hours)}</div>
              </div>
              <div>
                <div class="text-xs text-base-content/50">{t("workday.hours.worked")}</div>
                <div class="font-semibold tabular-nums">{fmt(p.worked_hours)}</div>
              </div>
              <div>
                <div class="text-xs text-base-content/50">{props.canSeeWages ? t("workday.hours.cost") : t("workday.hours.diff")}</div>
                <div class={["tabular-nums", !props.canSeeWages && Math.abs(diff) >= 0.5 ? (diff > 0 ? "text-warning" : "text-base-content/70") : ""].join(" ")}>
                  {props.canSeeWages
                    ? p.hourly_rate != null
                      ? (p.worked_hours * p.hourly_rate).toFixed(2)
                      : "—"
                    : `${diff > 0 ? "+" : ""}${fmt(diff)}`}
                </div>
              </div>
            </div>
            <span class="material-symbols-outlined text-base-content/40">{open.value ? "expand_less" : "expand_more"}</span>
          </button>
          {open.value && (
            <div class="border-t border-base-300 px-4 pb-4">
              {rows.value.length === 0 && <p class="py-4 text-sm text-base-content/50">{t("workday.hours.nothing_this_week")}</p>}
              <ul class="divide-y divide-base-200">
                {rows.value.map((r) => {
                  const s = plannedOf(r);
                  const e = entryOf(r);
                  return (
                    <li key={`${r.date}-${s?.shift_id ?? "x"}-${e?.id ?? "x"}`} class="flex flex-wrap items-center gap-x-4 gap-y-1 py-3 text-sm">
                      <div class="w-28 font-medium">{dayLabel(r.date)}</div>
                      <div class="w-36 text-base-content/70">
                        {s ? `${s.start_time.slice(0, 5)}–${s.end_time.slice(0, 5)}${s.role_name ? ` · ${s.role_name}` : ""}` : t("workday.hours.not_planned")}
                      </div>
                      <div class="w-36 tabular-nums">
                        {e ? (
                          <span class="inline-flex items-center gap-1">
                            {`${wallTime(e.clock_in, props.timezone).time}–${e.clock_out ? wallTime(e.clock_out, props.timezone).time : "…"}`}
                            {e.source === "manager" && (
                              <span class="material-symbols-outlined text-sm text-base-content/40" title={t("workday.hours.added_by_manager")}>
                                edit_note
                              </span>
                            )}
                          </span>
                        ) : (
                          <span class="text-base-content/40">{t("workday.hours.no_clock")}</span>
                        )}
                      </div>
                      <div class="w-12 text-right tabular-nums">{e ? fmt(e.hours) : ""}</div>
                      <div class="flex flex-1 flex-wrap gap-1">
                        {r.flags.map((f) => (
                          <span key={f} class={["badge badge-sm", FLAG_STYLE[f]].join(" ")}>
                            {flagText(f, r)}
                          </span>
                        ))}
                        {e?.note && <span class="text-xs italic text-base-content/50">{e.note}</span>}
                      </div>
                      {e ? (
                        <button class="btn btn-ghost btn-xs" onClick={() => props.onEdit(e)}>
                          <span class="material-symbols-outlined text-base">edit</span>
                          {t("workday.common.edit")}
                        </button>
                      ) : (
                        s && (
                          <button
                            class="btn btn-ghost btn-xs"
                            onClick={() => props.onAdd({ date: s.date, start: s.start_time.slice(0, 5), end: s.end_time.slice(0, 5), shift_id: s.shift_id })}
                          >
                            <span class="material-symbols-outlined text-base">more_time</span>
                            {t("workday.hours.add_entry")}
                          </button>
                        )
                      )}
                    </li>
                  );
                })}
              </ul>
              <button class="btn btn-ghost btn-sm mt-1" onClick={() => props.onAdd(null)}>
                <span class="material-symbols-outlined text-base">add</span>
                {t("workday.hours.add_other")}
              </button>
            </div>
          )}
        </div>
      );
    };
  },
});
